import { t, localeTag } from './i18n.js';
import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { X, LoaderCircle, Check, AlertCircle } from 'lucide-react';
import logoOnLight from './assets/brand/vename-fond-clair.svg';
import logoOnDark from './assets/brand/vename-fond-sombre.svg';
import logoSymbol from './assets/brand/vename-symbole.svg';
export function IconButton({
  label,
  children,
  onClick,
  active = false,
  disabled = false,
  className = '',
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      className={`icon-button ${active ? 'active' : ''} ${className}`}
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
export function Avatar({
  name,
  size = 'normal',
}: {
  name: string;
  size?: 'small' | 'normal' | 'large';
}) {
  const colors = ['lilac', 'peach', 'mint', 'blue'];
  const c = colors[(name.charCodeAt(0) || 0) % 4];
  return (
    <span aria-hidden="true" className={`avatar ${size} ${c}`}>
      {name
        .split(/[\s@]+/)
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase()}
    </span>
  );
}
/**
 * Official Vename signature from the brand kit, never redrawn: the dark-background version on navy
 * surfaces, the symbol alone for small identity spots. The product name sits outside the logo.
 */
export function Logo({ compact = false, onDark = false }: { compact?: boolean; onDark?: boolean }) {
  if (compact)
    return (
      <span className="brand">
        <img className="brand-symbol" src={logoSymbol} alt="Vename" />
      </span>
    );
  return (
    <span className="brand">
      <img className="brand-logo" src={onDark ? logoOnDark : logoOnLight} alt="Vename" />
      <span className="brand-product">Mail</span>
    </span>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
  initialFocus,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    id = useId();
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.showModal();
    initialFocus?.current?.focus();
    return () => {
      ref.current?.close();
      if (trigger?.isConnected) {
        requestAnimationFrame(() => {
          if (trigger.isConnected) trigger.focus();
        });
      }
    };
  }, [initialFocus]);
  return (
    <dialog
      ref={ref}
      className={`modal ${wide ? 'wide' : ''}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <header className="modal-head">
        <h2 id={id}>{title}</h2>
        <IconButton label={t('Fermer')} onClick={onClose}>
          <X size={19} />
        </IconButton>
      </header>
      {children}
    </dialog>
  );
}
export function Notice({
  children,
  type = 'info',
}: {
  children: ReactNode;
  type?: 'info' | 'error' | 'success';
}) {
  return (
    <div className={`notice ${type}`} role={type === 'error' ? 'alert' : 'status'}>
      {type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
      <div>{children}</div>
    </div>
  );
}
export function Loading({ label = t('Chargement…') }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <LoaderCircle className="spin" size={22} />
      <span>{label}</span>
    </div>
  );
}
/**
 * Compact size, in the same decimal units as quotas and subscriptions (1 Go = 1 milliard
 * d'octets), so that one mailbox reads the same on every screen.
 */
export function formatBytes(n: number) {
  if (n < 1000) return `${n} ${t('o')}`;
  if (n < 1000 ** 2)
    return `${(n / 1000).toLocaleString(localeTag(), { maximumFractionDigits: 0 })} ${t('Ko')}`;
  if (n < 1000 ** 3)
    return `${(n / 1000 ** 2).toLocaleString(localeTag(), { maximumFractionDigits: 1 })} ${t('Mo')}`;
  return `${(n / 1000 ** 3).toLocaleString(localeTag(), { maximumFractionDigits: 1 })} ${t('Go')}`;
}
export function displayDate(s: string, full = false) {
  const d = new Date(s);
  return new Intl.DateTimeFormat(
    localeTag(),
    full
      ? { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : d.toDateString() === new Date().toDateString()
        ? { hour: '2-digit', minute: '2-digit' }
        : { day: 'numeric', month: 'short' },
  ).format(d);
}

export function formatStorageBytes(value: number | string, maxFractionDigits = 3) {
  // PostgreSQL returns bigint columns as strings: "0" must read as zero, not NaN.
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return t('0 octet');
  if (n === 1) return t('1 octet');
  const index = Math.min(3, Math.floor(Math.log(n) / Math.log(1000)));
  return `${(n / 1000 ** index).toLocaleString(localeTag(), { maximumFractionDigits: maxFractionDigits })} ${[t('octets'), t('Ko'), t('Mo'), t('Go')][index]}`;
}
